import { useAuthEvent } from '@bedrock/auth';
import { isPrivateRelayEmail } from '@bedrock/auth';
import type { AuthEvent } from '@bedrock/auth';
import { AnalyticsController } from 'analytics/analytics-controller';
import {
  GTM_AUTH_SIGN_UP_APPLE,
  GTM_AUTH_SIGN_UP_APPLE_OPEN_EMAIL,
  GTM_AUTH_SIGN_UP_APPLE_PRIVATE_EMAIL,
  GTM_AUTH_SIGN_UP_FACEBOOK,
  GTM_AUTH_SIGN_UP_GENERAL,
  GTM_AUTH_SIGN_UP_GOOGLE,
  GTM_AUTH_SIGN_UP_NORMAL_EMAIL,
  GTM_AUTH_SOCIAL_PHONE_COLLECTION_COMPLETE,
  GTM_AUTH_SOCIAL_PHONE_COLLECTION_START,
  GTM_AUTH_SOCIAL_SIGN_IN_GENERAL,
  GTM_AUTH_SIGN_IN_EMAIL,
  GTM_AUTH_SIGN_IN_GOOGLE,
  GTM_AUTH_SIGN_IN_FACEBOOK,
  GTM_AUTH_SIGN_IN_APPLE,
  GTM_AUTH_SIGN_IN_APPLE_OPEN_EMAIL,
  GTM_AUTH_SIGN_IN_APPLE_PRIVATE_EMAIL,
} from 'constants/events';

function assertNever(x: never): never {
  throw new Error(`Unhandled auth event: ${(x as AuthEvent).type}`);
}

function trackSocialProviderSignUp(provider: string, email?: string): void {
  AnalyticsController.trackEvent(GTM_AUTH_SIGN_UP_GENERAL);
  if (provider === 'google') {
    AnalyticsController.trackEvent(GTM_AUTH_SIGN_UP_GOOGLE);
  } else if (provider === 'facebook') {
    AnalyticsController.trackEvent(GTM_AUTH_SIGN_UP_FACEBOOK);
  } else if (provider === 'apple') {
    AnalyticsController.trackEvent(GTM_AUTH_SIGN_UP_APPLE);
    AnalyticsController.trackEvent(
      isPrivateRelayEmail(email ?? '') ? GTM_AUTH_SIGN_UP_APPLE_PRIVATE_EMAIL : GTM_AUTH_SIGN_UP_APPLE_OPEN_EMAIL
    );
  }
}

/**
 * Maps every auth domain event to its GTM equivalent.
 *
 * The exhaustive switch guarantees a compile error if a new AuthEvent type is
 * added to the package without a conscious tracking decision being made here.
 * Events that are intentionally not tracked fall through to an explicit `return`.
 */
function handleAuthEvent(event: AuthEvent): void {
  switch (event.type) {
    case 'sign_up_complete':
      AnalyticsController.trackEvent(GTM_AUTH_SIGN_UP_GENERAL);
      AnalyticsController.trackEvent(GTM_AUTH_SIGN_UP_NORMAL_EMAIL);
      if (event.newsletterConsent) {
        AnalyticsController.trackEventConditionally({ event: 'newsletter-signup-register' }, true);
      }
      return;

    case 'needs_phone':
      // Only track the GTM funnel event for social sign-ups (provider present).
      // Email/password and password-reset flows that need phone are not social events.
      if (event.provider) {
        AnalyticsController.trackEvent(GTM_AUTH_SOCIAL_PHONE_COLLECTION_START);
      }
      return;

    case 'social_phone_collection_complete':
      AnalyticsController.trackEvent(GTM_AUTH_SOCIAL_PHONE_COLLECTION_COMPLETE);
      trackSocialProviderSignUp(event.provider, event.user.email);
      return;

    case 'email_sign_in_complete':
      AnalyticsController.trackEvent(GTM_AUTH_SIGN_IN_EMAIL);
      return;

    case 'social_sign_in_complete':
      AnalyticsController.trackEvent(GTM_AUTH_SOCIAL_SIGN_IN_GENERAL);
      if (event.provider === 'google') {
        AnalyticsController.trackEvent(GTM_AUTH_SIGN_IN_GOOGLE);
      } else if (event.provider === 'facebook') {
        AnalyticsController.trackEvent(GTM_AUTH_SIGN_IN_FACEBOOK);
      } else if (event.provider === 'apple') {
        AnalyticsController.trackEvent(GTM_AUTH_SIGN_IN_APPLE);
        AnalyticsController.trackEvent(
          isPrivateRelayEmail(event.user.email ?? '') ? GTM_AUTH_SIGN_IN_APPLE_PRIVATE_EMAIL : GTM_AUTH_SIGN_IN_APPLE_OPEN_EMAIL
        );
      }
      return;

    // Intentionally not tracked — presence in this list is the decision record.
    case 'auth_complete':
    case 'migrated_reset_complete':
    case 'needs_phone_post_auth':
      return;

    default:
      assertNever(event);
  }
}

/**
 * Subscribes to all auth domain events and fires the corresponding GTM events.
 *
 * Mounted once at the app level inside PostAuthActionHandler, so subscriptions
 * survive modal unmounts and IDP redirects.
 */
export function useAuthAnalytics(): void {
  useAuthEvent('sign_up_complete', handleAuthEvent);
  useAuthEvent('email_sign_in_complete', handleAuthEvent);
  useAuthEvent('needs_phone', handleAuthEvent);
  useAuthEvent('needs_phone_post_auth', handleAuthEvent);
  useAuthEvent('social_phone_collection_complete', handleAuthEvent);
  useAuthEvent('social_sign_in_complete', handleAuthEvent);
}
